import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    // Scalar's API reference writes hashes such as #tag/certificates/GET/api/v1/certificates and
    // scrolls to them itself. They are not valid selectors, so leave them alone: no element scroll,
    // no warning, and no saved-position restore, which would otherwise override Scalar's scroll
    // after the user has scrolled within a section.
    if (to.hash) {
      try {
        document.querySelector(to.hash)
      } catch {
        return false
      }
    }
    // If navigating with browser back/forward, restore saved position
    if (savedPosition) {
      return savedPosition
    }
    // If navigating to a hash anchor, scroll to that element
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    // Otherwise scroll to top
    return { top: 0 }
  },
  routes: [
    // Public routes
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/public/LandingView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },
    {
      path: '/thank-you',
      name: 'thank-you',
      component: () => import('@/views/public/ThankYouView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('@/views/public/HelpView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/docs/:page?',
      name: 'docs',
      component: () => import('@/views/public/DocsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/faq',
      name: 'faq',
      redirect: { name: 'help' },
      meta: { requiresAuth: false }
    },
    {
      path: '/privacy',
      name: 'privacy',
      redirect: { name: 'help', query: { tab: 'privacy' } },
      meta: { requiresAuth: false }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/public/TermsView.vue'),
      meta: { requiresAuth: false }
    },
{
      path: '/status',
      name: 'status',
      component: () => import('@/views/public/StatusView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/error',
      name: 'error',
      component: () => import('@/views/public/ErrorView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/processing-error',
      name: 'processing-error',
      component: () => import('@/views/public/ProcessingErrorView.vue'),
      meta: { requiresAuth: false }
    },
    // Certificate form at /c/:id (new canonical URL)
    {
      path: '/c/:id',
      name: 'certificate',
      component: () => import('@/views/public/HomeView.vue'),
      meta: { requiresAuth: false }
    },
    // BACKWARD COMPATIBILITY: Redirect old GUID URLs to new /c/:id format
    {
      path: '/:id([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})',
      name: 'document-legacy',
      redirect: to => ({ name: 'certificate', params: { id: to.params.id } }),
      meta: { requiresAuth: false }
    },

    // Authentication routes - Combined login/register page
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/account/AuthView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/account/AuthView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },
    {
      path: '/login/2fa',
      name: 'login-2fa',
      component: () => import('@/views/account/TwoFactorChallengeView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/account/2fa-required',
      name: '2fa-required',
      component: () => import('@/views/account/TwoFactorRequiredView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/signup',
      redirect: { name: 'register' }
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/account/OAuthCallbackView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/account/ForgotPasswordView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/account/ResetPasswordView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/confirm-email',
      name: 'confirm-email',
      component: () => import('@/views/account/ConfirmEmailView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/confirm-email-change',
      name: 'confirm-email-change',
      component: () => import('@/views/account/ConfirmEmailChangeView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/accept-invite',
      name: 'accept-invite',
      component: () => import('@/views/account/AcceptInviteView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },

    // User dashboard (for non-admin users)
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/dashboard/DashboardView.vue'),
      meta: { requiresAuth: true }
    },

    // Customer's own certificates (matched by email)
    {
      path: '/my-certificates',
      name: 'my-certificates',
      component: () => import('@/views/certificates/MyCertificatesView.vue'),
      meta: { requiresAuth: true }
    },

    // Onboarding flow (for new users)
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('@/views/onboarding/OnboardingView.vue'),
      meta: { requiresAuth: true }
    },

    // Account management routes (authenticated)
    {
      path: '/account',
      component: () => import('@/layouts/AccountLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'account',
          component: () => import('@/views/account/ProfileView.vue')
        },
        {
          path: 'email',
          name: 'account-email',
          component: () => import('@/views/account/EmailView.vue')
        },
        {
          path: 'password',
          name: 'account-password',
          component: () => import('@/views/account/PasswordView.vue')
        },
        {
          path: 'two-factor',
          name: 'account-2fa',
          component: () => import('@/views/account/TwoFactorView.vue')
        },
        {
          path: 'personal-data',
          name: 'account-data',
          component: () => import('@/views/account/PersonalDataView.vue')
        },
        {
          path: 'delete-account',
          name: 'account-delete',
          component: () => import('@/views/account/DeleteAccountView.vue')
        },
        {
          path: 'authenticator-setup',
          name: 'account-authenticator',
          component: () => import('@/views/account/AuthenticatorSetupView.vue')
        },
        // Legacy redirect for old security route
        {
          path: 'security',
          redirect: { name: 'account-password' }
        }
      ]
    },

    // Certificate management routes (permission-based)
    {
      path: '/certificates',
      name: 'certificates',
      component: () => import('@/views/certificates/CertificatesListView.vue'),
      meta: { requiresAuth: true, requiresPermission: 'ViewCertificates' }
    },
    // Archived certificates
    {
      path: '/certificates/archived',
      name: 'certificates-archived',
      component: () => import('@/views/certificates/ArchivedCertificatesView.vue'),
      meta: { requiresAuth: true, requiresPermission: 'ViewCertificates' }
    },
    // New wizard-based single certificate request (default)
    {
      path: '/certificates/request',
      name: 'certificates-request',
      component: () => import('@/views/certificates/RequestCertificateWizard.vue'),
      meta: { requiresAuth: true, requiresPermission: 'RequestCertificates' }
    },
    // Bulk request mode (existing workflow)
    {
      path: '/certificates/request/bulk',
      name: 'certificates-request-bulk',
      component: () => import('@/views/admin/ContactsView.vue'),
      meta: { requiresAuth: true, requiresPermission: 'RequestCertificates' }
    },
    // Bulk request send step
    {
      path: '/certificates/request/send',
      name: 'certificates-request-send',
      component: () => import('@/views/admin/RequestDocumentsView.vue'),
      meta: { requiresAuth: true, requiresPermission: 'RequestCertificates' }
    },

    // Organization routes
    {
      path: '/organizations',
      name: 'organizations',
      component: () => import('@/views/organization/OrganizationsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/organization/new',
      name: 'organization-create',
      component: () => import('@/views/organization/CreateOrganizationView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/organization/settings',
      meta: { requiresAuth: true, requiresPermission: 'ViewOrgSettings' },
      children: [
        { path: '', name: 'organization-settings', redirect: { name: 'organization-settings-general' } },
        { path: 'general', name: 'organization-settings-general', component: () => import('@/views/admin/ConfigView.vue') },
        { path: 'members', name: 'organization-settings-members', component: () => import('@/views/admin/ConfigView.vue') },
        { path: 'integrations', name: 'organization-settings-integrations', component: () => import('@/views/admin/ConfigView.vue') },
        { path: 'email', name: 'organization-settings-email', component: () => import('@/views/admin/ConfigView.vue') },
        { path: 'security', name: 'organization-settings-security', component: () => import('@/views/admin/ConfigView.vue') },
        { path: 'expiration', name: 'organization-settings-expiration', component: () => import('@/views/admin/ConfigView.vue') },
        { path: 'billing', name: 'organization-settings-billing', component: () => import('@/views/admin/ConfigView.vue') },
      ]
    },
    {
      path: '/organization/members',
      redirect: { name: 'organization-settings-members' }
    },
    {
      path: '/organization/config',
      redirect: { name: 'organization-settings' }
    },

    // Legacy organization routes with ID (redirect to current org)
    {
      path: '/organization/:id/settings',
      redirect: { name: 'organization-settings' }
    },
    {
      path: '/organization/:id/members',
      redirect: { name: 'organization-settings-members' }
    },

    // Legacy admin route redirects (backward compatibility)
    {
      path: '/admin',
      redirect: { name: 'certificates' }
    },
    {
      path: '/admin/contacts',
      redirect: { name: 'certificates' }
    },
    {
      path: '/admin/request',
      redirect: { name: 'certificates-request' }
    },
    {
      path: '/admin/config',
      redirect: (to) => {
        const query = { ...to.query }
        delete query.tab
        return { name: 'organization-settings', query }
      }
    },
    {
      path: '/admin/settings',
      redirect: (to) => {
        const tab = to.query.tab as string
        const tabRouteMap: Record<string, string> = {
          general: 'organization-settings-general',
          members: 'organization-settings-members',
          integrations: 'organization-settings-integrations',
          email: 'organization-settings-email',
          security: 'organization-settings-security',
          expiration: 'organization-settings-expiration',
        }
        const name = tabRouteMap[tab] ?? 'organization-settings'
        const query = { ...to.query }
        delete query.tab
        return { name, query }
      }
    },

    // Catch-all 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/public/NotFoundView.vue')
    }
  ]
})

// Permission checking helper
import type { OrganizationPermission } from '@/composables/usePermissions'
import type { OrganizationRole } from '@/types'

const rolePermissions: Record<OrganizationRole, Set<OrganizationPermission>> = {
  Owner: new Set([
    'ViewCertificates',
    'RequestCertificates',
    'ManageContacts',
    'ViewOrgSettings',
    'EditOrgSettings',
    'InviteMembers',
    'ManageMembers',
    'RemoveMembers',
    'DeleteOrganization',
    'TransferOwnership',
    'ViewBilling',
    'ManageBilling'
  ]),
  Admin: new Set([
    'ViewCertificates',
    'RequestCertificates',
    'ManageContacts',
    'ViewOrgSettings',
    'EditOrgSettings',
    'InviteMembers',
    'ManageMembers',
    'RemoveMembers',
    'ViewBilling'
  ]),
  Member: new Set(['ViewCertificates', 'RequestCertificates', 'ManageContacts']),
  Viewer: new Set(['ViewCertificates'])
}

function hasPermission(
  authStore: ReturnType<typeof useAuthStore>,
  permission: OrganizationPermission
): boolean {
  const role = authStore.currentOrganization?.role
  if (!role) return false
  return rolePermissions[role]?.has(permission) ?? false
}

// Helper to determine post-login destination
function getPostLoginDestination(authStore: ReturnType<typeof useAuthStore>): string {
  // Use server-side onboarding status
  if (!authStore.user?.onboardingCompleted) {
    return '/onboarding'
  }

  // All authenticated users go to dashboard
  return '/dashboard'
}

// Navigation guards
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  // Wait for auth check if not initialized
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  // Redirect authenticated users away from guest-only pages
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    return getPostLoginDestination(authStore)
  }

  // Redirect unauthenticated users to login
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return {
      name: 'login',
      query: { redirect: to.fullPath }
    }
  }

  // Check permission-based route access
  if (to.meta.requiresPermission) {
    const permission = to.meta.requiresPermission as OrganizationPermission

    // If no organization is selected, redirect to organization selection
    if (!authStore.currentOrganization) {
      return { name: 'organizations' }
    }

    // Check if user has the required permission
    if (!hasPermission(authStore, permission)) {
      return { name: 'dashboard' }
    }
  }
})

export default router
